<template>
	<div
		class="pronto-group-icon"
		:class="{ chrome: isChrome, firefox: isFirefox, safari: isSafari }"
		ref="el"
	>
		<img
			:src="isVisible ? imageIconUrl : placeholderSrc"
			class="group-icon-image"
			v-if="imageIconUrl"
		/>
		<div class="group-icon-emoji" v-else-if="emojiIcon" v-text="emojiIcon" />
		<UserGroupIcon v-else class="group-icon-fallback" type="solid" />
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getChat } from '@/store/chats'
import { useEventBus } from '@/helpers/bus'
import { UserGroupIcon } from '@/icons'
import { isChrome, isFirefox, isSafari } from '@/store/browser'

const placeholderSrc =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const props = defineProps({
	chatId: [Number, String],
	imageUrl: String,
	emoji: String,
})
const chat = ref(getChat(props.chatId))
const el = ref(null)
const isVisible = ref(false)
let observer = null

useEventBus('chat-update', () => {
	chat.value = getChat(props.chatId)
})

onMounted(() => {
	observer = new IntersectionObserver(
		(entries) => {
			if (entries[0].isIntersecting) {
				isVisible.value = true
				observer.disconnect()
				observer = null
			}
		},
		{ rootMargin: '200px' }
	)
	observer.observe(el.value)
})

onBeforeUnmount(() => {
	observer?.disconnect()
})

const imageIconUrl = computed(
	() => props.imageUrl || chat.value?.icon_image_url
)
const emojiIcon = computed(() => props.emoji || chat.value?.icon_emoji)
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-group-icon {
	width: 1em;
	height: 1em;
	background-color: var(--neutral-200);
	@include flex-center;
	@include radius;

	// Hacktown: emoji rendering across browsers is sucks ⚾🏀
	&.chrome {
		.group-icon-emoji {
			margin-left: -0.05em;
		}
	}
	&.safari {
		.group-icon-emoji {
			margin-top: -0.1em;
		}
	}
	&.firefox {
		.group-icon-emoji {
			margin-left: -0.05em;
			margin-top: 0.1em;
		}
	}

	// hack in support for high resolution displays, like macbook retina because
	// not only do these emojis render differently across browsers, but each
	// browser renders them differently on high resolution displays.
	@media (min-resolution: 2x) {
		.group-icon-emoji {
			font-size: 0.72em !important;
		}
		&.chrome {
			.group-icon-emoji {
				margin-top: 0 !important;
				margin-top: 0.1em !important;
			}
		}
		&.safari {
			.group-icon-emoji {
				margin-top: 0 !important;
			}
		}
		&.firefox {
			.group-icon-emoji {
				margin-left: 0 !important;
				margin-top: 0.1em !important;
			}
		}
	}

	.group-icon-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		overflow: hidden;
		@include radius;
	}
	.group-icon-fallback,
	.group-icon-emoji {
		color: var(--neutral-500);
		flex-shrink: 0;
		overflow: hidden;
		font-size: 0.66em;
	}
}
</style>
